import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { calculateExpression } from "../utils/calculator";

type Props = { id: string };

function NumberField({ label, value, onChange, suffix }: { label: string; value: number; onChange: (value: number) => void; suffix?: string }) {
  return (
    <label className="field">
      <span>{label}</span>
      <div className="input-with-suffix">
        <input type="number" inputMode="decimal" value={value} onChange={(event) => onChange(Number(event.target.value) || 0)} />
        {suffix && <b>{suffix}</b>}
      </div>
    </label>
  );
}

function Calculator() {
  const [expression, setExpression] = useState("(1200 + 380) * 0.9");
  const [result, setResult] = useState("1422");
  const run = () => {
    try {
      setResult(String(Number(calculateExpression(expression).toFixed(10))));
    } catch (error) {
      setResult(error instanceof Error ? error.message : "计算失败");
    }
  };
  return (
    <section className="workbench">
      <label className="field"><span>计算表达式</span><input value={expression} onChange={(event) => setExpression(event.target.value)} onKeyDown={(event) => event.key === "Enter" && run()} /></label>
      <button className="primary-button" onClick={run}>计算结果</button>
      <div className="result-box"><small>结果</small><strong>{result}</strong></div>
    </section>
  );
}

function TextTools() {
  const [text, setText] = useState("把需要处理的文字粘贴到这里。");
  const count = useMemo(() => ({ characters: text.length, noSpace: text.replace(/\s/g, "").length, lines: text ? text.split(/\r?\n/).length : 0, words: text.trim() ? text.trim().split(/\s+/).length : 0 }), [text]);
  const transform = (kind: "trim" | "upper" | "lower" | "dedupe") => {
    if (kind === "trim") setText(text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean).join("\n"));
    if (kind === "upper") setText(text.toUpperCase());
    if (kind === "lower") setText(text.toLowerCase());
    if (kind === "dedupe") setText([...new Set(text.split(/\r?\n/))].join("\n"));
  };
  return (
    <section className="workbench">
      <label className="field"><span>文字内容</span><textarea rows={9} value={text} onChange={(event) => setText(event.target.value)} /></label>
      <div className="stat-row"><b>{count.characters} 字符</b><b>{count.noSpace} 不含空格</b><b>{count.lines} 行</b><b>{count.words} 词</b></div>
      <div className="button-row"><button onClick={() => transform("trim")}>清理空行</button><button onClick={() => transform("dedupe")}>行去重</button><button onClick={() => transform("upper")}>英文大写</button><button onClick={() => transform("lower")}>英文小写</button><button onClick={() => navigator.clipboard.writeText(text)}>复制</button></div>
    </section>
  );
}

function TodayEat() {
  const [items, setItems] = useState("米饭快餐\n面条\n饺子\n轻食\n火锅\n粤菜");
  const [choice, setChoice] = useState("点击开始，替你做决定");
  const choose = () => {
    const values = items.split(/\r?\n|，|,/).map((item) => item.trim()).filter(Boolean);
    setChoice(values.length ? values[crypto.getRandomValues(new Uint32Array(1))[0] % values.length] : "请先添加候选项");
  };
  return (
    <section className="workbench">
      <label className="field"><span>候选项（每行一个）</span><textarea rows={7} value={items} onChange={(event) => setItems(event.target.value)} /></label>
      <button className="primary-button" onClick={choose}>今天就吃这个</button>
      <div className="result-box accent"><small>随机结果</small><strong>{choice}</strong></div>
    </section>
  );
}

function Budget() {
  const [income, setIncome] = useState(12000);
  const [fixed, setFixed] = useState(4500);
  const [daily, setDaily] = useState(2800);
  const [other, setOther] = useState(1000);
  const balance = income - fixed - daily - other;
  const savingsRate = income > 0 ? balance / income * 100 : 0;
  return (
    <section className="workbench">
      <div className="form-grid"><NumberField label="每月收入" value={income} onChange={setIncome} suffix="元" /><NumberField label="固定支出" value={fixed} onChange={setFixed} suffix="元" /><NumberField label="日常支出" value={daily} onChange={setDaily} suffix="元" /><NumberField label="其他支出" value={other} onChange={setOther} suffix="元" /></div>
      <div className="result-grid"><div><small>月结余</small><strong className={balance < 0 ? "negative" : ""}>{balance.toLocaleString()} 元</strong></div><div><small>储蓄率</small><strong>{savingsRate.toFixed(1)}%</strong></div></div>
    </section>
  );
}

function RateConverter() {
  const [amount, setAmount] = useState(100);
  const [rate, setRate] = useState(7.2);
  return (
    <section className="workbench">
      <div className="form-grid"><NumberField label="原始金额" value={amount} onChange={setAmount} /><NumberField label="自定义汇率" value={rate} onChange={setRate} /></div>
      <div className="result-box"><small>换算结果</small><strong>{(amount * rate).toLocaleString(undefined, { maximumFractionDigits: 4 })}</strong></div>
    </section>
  );
}

function PromptGenerator() {
  const [role, setRole] = useState("资深产品经理");
  const [task, setTask] = useState("分析一个面向年轻人的生活工具产品");
  const [format, setFormat] = useState("用表格给出结论，并列出三条下一步行动");
  const prompt = `你是一名${role}。请${task}。先说明判断依据，再指出风险与缺失信息，最后${format}。若信息不足，请明确写出假设，不要编造事实。`;
  return (
    <section className="workbench">
      <label className="field"><span>角色</span><input value={role} onChange={(event) => setRole(event.target.value)} /></label>
      <label className="field"><span>任务</span><textarea rows={3} value={task} onChange={(event) => setTask(event.target.value)} /></label>
      <label className="field"><span>输出要求</span><input value={format} onChange={(event) => setFormat(event.target.value)} /></label>
      <div className="copy-panel"><p>{prompt}</p><button onClick={() => navigator.clipboard.writeText(prompt)}>复制提示词</button></div>
    </section>
  );
}

function ValueHelper() {
  const [price, setPrice] = useState(4999);
  const [years, setYears] = useState(2);
  const [condition, setCondition] = useState(0.85);
  const value = Math.max(0, price * Math.pow(0.72, years) * condition);
  return (
    <section className="workbench">
      <div className="form-grid"><NumberField label="原购买价" value={price} onChange={setPrice} suffix="元" /><NumberField label="使用年数" value={years} onChange={setYears} suffix="年" /></div>
      <label className="field"><span>成色系数：{condition.toFixed(2)}</span><input type="range" min="0.5" max="1" step="0.05" value={condition} onChange={(event) => setCondition(Number(event.target.value))} /></label>
      <div className="result-box"><small>参考估值</small><strong>约 {Math.round(value).toLocaleString()} 元</strong></div>
    </section>
  );
}

function IncomeModel() {
  const [fixed, setFixed] = useState(8000);
  const [price, setPrice] = useState(30);
  const [variable, setVariable] = useState(12);
  const [volume, setVolume] = useState(700);
  const contribution = price - variable;
  const breakEven = contribution > 0 ? Math.ceil(fixed / contribution) : 0;
  const profit = contribution * volume - fixed;
  return (
    <section className="workbench">
      <div className="form-grid"><NumberField label="每月固定成本" value={fixed} onChange={setFixed} suffix="元" /><NumberField label="单价" value={price} onChange={setPrice} suffix="元" /><NumberField label="单件变动成本" value={variable} onChange={setVariable} suffix="元" /><NumberField label="预计销量" value={volume} onChange={setVolume} suffix="件" /></div>
      <div className="result-grid"><div><small>保本销量</small><strong>{breakEven || "—"} 件</strong></div><div><small>预计利润</small><strong className={profit < 0 ? "negative" : ""}>{profit.toLocaleString()} 元</strong></div></div>
    </section>
  );
}

function AnswerHelper() {
  const [items, setItems] = useState("去运动\n先睡一觉\n完成最小的一步\n找朋友聊聊");
  const [answer, setAnswer] = useState("写下选项，让随机性打破犹豫");
  const choose = () => {
    const options = items.split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
    setAnswer(options.length ? options[crypto.getRandomValues(new Uint32Array(1))[0] % options.length] : "没有可选答案");
  };
  return <section className="workbench"><label className="field"><span>备选答案</span><textarea rows={6} value={items} onChange={(event) => setItems(event.target.value)} /></label><button className="primary-button" onClick={choose}>帮我选一个</button><div className="result-box accent"><small>这次的答案</small><strong>{answer}</strong></div></section>;
}

function QrTool() {
  const [text, setText] = useState("https://example.com");
  const [image, setImage] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    QRCode.toDataURL(text || " ", { width: 520, margin: 2, color: { dark: "#17211d", light: "#ffffff" } })
      .then((value) => active && setImage(value))
      .catch(() => active && setError("二维码生成失败"));
    return () => { active = false; };
  }, [text]);
  return (
    <section className="workbench qr-layout">
      <div><label className="field"><span>文字或网址</span><textarea rows={5} value={text} onChange={(event) => setText(event.target.value)} /></label></div>
      <div className="qr-card">{image && <img src={image} alt="生成的二维码" />}{error && <p>{error}</p>}<a className="secondary-button" href={image} download="xiaoming-qrcode.png">下载 PNG</a></div>
    </section>
  );
}

function ImageConvert() {
  const [source, setSource] = useState<File | null>(null);
  const [format, setFormat] = useState<"image/jpeg" | "image/png" | "image/webp">("image/jpeg");
  const [quality, setQuality] = useState(0.88);
  const [output, setOutput] = useState("");
  useEffect(() => () => { if (output) URL.revokeObjectURL(output); }, [output]);
  const convert = async () => {
    if (!source) return;
    const bitmap = await createImageBitmap(source);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, format, quality));
    bitmap.close();
    if (!blob) return;
    if (output) URL.revokeObjectURL(output);
    setOutput(URL.createObjectURL(blob));
  };
  const extension = format.split("/")[1].replace("jpeg", "jpg");
  return (
    <section className="workbench">
      <label className="file-drop"><input type="file" accept="image/*" onChange={(event) => setSource(event.target.files?.[0] || null)} /><b>{source ? source.name : "选择一张图片"}</b></label>
      <div className="form-grid"><label className="field"><span>输出格式</span><select value={format} onChange={(event) => setFormat(event.target.value as typeof format)}><option value="image/jpeg">JPG</option><option value="image/png">PNG</option><option value="image/webp">WebP</option></select></label><label className="field"><span>质量：{Math.round(quality * 100)}%</span><input type="range" min="0.3" max="1" step="0.02" value={quality} onChange={(event) => setQuality(Number(event.target.value))} /></label></div>
      <button className="primary-button" onClick={convert} disabled={!source}>开始转换</button>
      {output && <a className="secondary-button standalone" href={output} download={`converted.${extension}`}>下载转换后的图片</a>}
    </section>
  );
}

function GameProgress() {
  const [current, setCurrent] = useState(38);
  const [target, setTarget] = useState(100);
  const [daily, setDaily] = useState(6);
  const remaining = Math.max(0, target - current);
  return <section className="workbench"><div className="form-grid"><NumberField label="当前进度" value={current} onChange={setCurrent} /><NumberField label="目标进度" value={target} onChange={setTarget} /><NumberField label="每天可完成" value={daily} onChange={setDaily} /></div><div className="result-box"><small>预计还需</small><strong>{daily > 0 ? Math.ceil(remaining / daily) : "—"} 天</strong></div></section>;
}

export function InteractiveTool({ id }: Props) {
  if (id === "calculator") return <Calculator />;
  if (id === "text-tools") return <TextTools />;
  if (id === "today-eat") return <TodayEat />;
  if (id === "budget") return <Budget />;
  if (id === "exchange-rate") return <RateConverter />;
  if (id === "prompt-generator") return <PromptGenerator />;
  if (id === "value-helper") return <ValueHelper />;
  if (id === "income-models") return <IncomeModel />;
  if (id === "answer-helper") return <AnswerHelper />;
  if (id === "code-tools") return <QrTool />;
  if (id === "image-convert") return <ImageConvert />;
  if (id === "game-speed-tools") return <GameProgress />;
  return null;
}

export const INTERACTIVE_TOOL_IDS = new Set([
  "calculator", "text-tools", "today-eat", "budget", "exchange-rate", "prompt-generator",
  "value-helper", "income-models", "answer-helper", "code-tools", "image-convert", "game-speed-tools",
]);
